import React from 'react';
import moment from 'moment/moment';
import Tooltip from '@mui/material/Tooltip';
import { DateFormats } from 'constants/common';
import { green } from '@mui/material/colors';
import { ChecklistRtl, CopyAll, Edit } from '@mui/icons-material';
import { FormControlLabel, IconButton, Stack, Switch, Typography, Box } from '@mui/material';

import { Template } from '../../api';
import type { ListSectionActions } from './ListSection';
import { capitalizeFirstLetter } from '../../../../formatters';

export function createColumns(actions: ListSectionActions, isActiveStatusSetting: boolean) {
  return [
    {
      sortable: true,
      accessor: 'name',
      Header: 'Template Name',
      headerClassName: 'customHeader',
      Cell: ({ original }: { original: Template }) => (
        <Stack direction="row" alignItems="center" gap="0.5rem">
          <Typography>{original.name}</Typography>

          {original.isActive && (
            <Tooltip title={`Active template for ${capitalizeFirstLetter(original.department.name)}`}>
              <Box
                sx={{
                  width: '0.7rem',
                  height: '0.7rem',
                  backgroundColor: green[300],
                  borderRadius: '50%',
                  animation: 'pulse 1.8s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                      backgroundColor: green[300],
                    },
                    '50%': {
                      transform: 'scale(1.15)',
                      backgroundColor: green[400],
                    },
                    '100%': {
                      transform: 'scale(1)',
                      backgroundColor: green[300],
                    },
                  },
                }}
              />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      sortable: true,
      accessor: 'name',
      Header: 'Created At',
      headerClassName: 'customHeader',
      Cell: ({ original }: { original: Template }) => (
        <Typography>{moment(Number(original.createdDateTime)).format(DateFormats.DateTimeMedium24Hour)}</Typography>
      ),
    },
    {
      sortable: true,
      accessor: 'name',
      Header: 'Last Updated',
      headerClassName: 'customHeader',
      Cell: ({ original }: { original: Template }) => (
        <Typography>
          {original.modifiedDateTime
            ? moment(Number(original.modifiedDateTime)).format(DateFormats.DateTimeMedium24Hour)
            : 'No modified'}
        </Typography>
      ),
    },
    {
      accessor: '',
      sortable: false,
      Header: 'Action',
      headerClassName: 'customHeader',
      Cell: ({ original }: { original: Template }) => (
        <Stack direction="row" gap="0.5rem" paddingLeft="0.5rem">
          <Tooltip
            title={
              original.isActive
                ? 'Active template'
                : 'When you activate a template, it will be used as the default template for new Vendor Opportunities'
            }
          >
            <FormControlLabel
              label="Active"
              control={
                <Switch
                  size="small"
                  checked={original.isActive}
                  disabled={original.isActive || isActiveStatusSetting}
                  onChange={async _ => {
                    await actions.onSetActiveStatus(original.businessProcessFlowId);
                  }}
                />
              }
            />
          </Tooltip>

          <Tooltip title="Copy template">
            <IconButton
              className="icon-btn-bordered"
              onClick={() => {
                actions.onCopyTemplate(original.businessProcessFlowId);
              }}
            >
              <CopyAll color="primary" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Close reasons">
            <IconButton
              className="icon-btn-bordered"
              onClick={() => {
                actions.onRedirectToCloseReasons(original.businessProcessFlowId);
              }}
            >
              <ChecklistRtl color="primary" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit template">
            <IconButton
              className="icon-btn-bordered"
              onClick={async () => {
                await actions.onEditTemplate(original.businessProcessFlowId);
              }}
            >
              <Edit color="primary" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];
}
